'use client';

import { useState, useEffect } from 'react';
import { isSupportedLanguage } from '@/services/languageDetection';
import { setUserLocaleClient, getUserLocaleClient, getBrowserLanguageClient } from '@/services/clientLocale';

/**
 * Hook to detect and manage browser language preferences
 */
export function useBrowserLanguage() {
  const [browserLanguage, setBrowserLanguage] = useState('en');
  const [isAutoDetected, setIsAutoDetected] = useState(false);

  useEffect(() => {
    const detectAndSetLanguage = () => {
      try {
        // Get browser language
        const detectedLanguage = getBrowserLanguageClient();
        setBrowserLanguage(detectedLanguage);

        // Get current locale
        const currentLocale = getUserLocaleClient();
        
        // Check if user has manually set a language preference
        const isManuallySet = localStorage.getItem('isManuallySetLocale') === 'true';

        // If user has manually selected a language, respect that choice completely
        if (isManuallySet) {
          const manualChoice = localStorage.getItem('manualLanguageChoice');
          return;
        }

        // Check if detected language is supported
        const isSupported = isSupportedLanguage(detectedLanguage);

        // More aggressive auto-set conditions
        const shouldAutoSet = (
          // First time or no locale
          !currentLocale || currentLocale === 'none' ||
          // Browser language is different and supported (regardless of manual flag if browser changed)
          (detectedLanguage !== currentLocale && isSupported) ||
          // Force if never auto-detected and browser isn't Korean
          (localStorage.getItem('hasAutoDetectedLanguage') !== 'true' && isSupported && detectedLanguage !== 'ko') ||
          // Force if current is Korean but browser is different
          (currentLocale === 'ko' && detectedLanguage !== 'ko' && isSupported)
        );

        if (shouldAutoSet) {
          // Set the detected language
          setUserLocaleClient(detectedLanguage);
          setIsAutoDetected(true);

          // Mark that this is auto-detected (not manually set)
          localStorage.setItem('hasAutoDetectedLanguage', 'true');
          localStorage.setItem('autoDetectedLanguage', detectedLanguage);
          localStorage.removeItem('isManuallySetLocale');
          localStorage.removeItem('manualLanguageChoice');
          localStorage.setItem('hotple-last-selected-language', detectedLanguage);

          // Determine correct mode based on detected language
          const { hasManualTranslations } = require('@/i18n/config');
          const correctMode = hasManualTranslations(detectedLanguage) ? 'manual' : 'browser';
          
          console.log(`[useBrowserLanguage] Auto-detected language ${detectedLanguage}, using mode: ${correctMode}`);

          // Trigger language change event with correct mode
          window.dispatchEvent(new CustomEvent('languageChanged', { detail: { language: detectedLanguage, mode: correctMode } }));
        }
      } catch (error) {
        console.error('Error detecting browser language:', error);
      }
    };

    // Run detection on mount and when language changes
    detectAndSetLanguage();

    // Listen for browser language change events
    window.addEventListener('languagechange', detectAndSetLanguage);

    // Listen for language change events
    const handleLanguageChange = (event) => {
      const { mode } = event.detail;
      setBrowserLanguage(getBrowserLanguageClient());
      
      if (mode === 'browser') {
        setIsAutoDetected(true);
      } else {
        setIsAutoDetected(false);
      }
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    
    return () => {
      window.removeEventListener('languagechange', detectAndSetLanguage);
      window.removeEventListener('languageChanged', handleLanguageChange);
    };
  }, []);

  const setLanguageManually = (languageCode) => {
    try {
      setUserLocaleClient(languageCode);

      // Always mark as manually set when user explicitly chooses a language
      localStorage.setItem('isManuallySetLocale', 'true');
      localStorage.setItem('manualLanguageChoice', languageCode);
      localStorage.setItem('hotple-last-selected-language', languageCode);
      localStorage.setItem('hasAutoDetectedLanguage', 'false');
      setIsAutoDetected(false);

      // No reload - instant change
    } catch (error) {
      console.error('Error setting language manually:', error);
    }
  };

  const resetLanguageDetection = () => {
    localStorage.removeItem('isManuallySetLocale');
    localStorage.removeItem('hasAutoDetectedLanguage');
    localStorage.removeItem('autoDetectedLanguage');
    localStorage.removeItem('userLocale');
    // Clear cookie
    document.cookie = 'NEXT_LOCALE=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT;';

    // Force reload after clearing
    setTimeout(() => {
      window.location.reload();
    }, 500);
  };

  const forceLanguageDetection = () => {
    // Clear any blocking flags
    Object.keys(window).forEach(key => {
      if (key.startsWith('languageDetection')) {
        delete window[key];
      }
    });

    // Clear auto-detection flag to force re-detection
    localStorage.removeItem('hasAutoDetectedLanguage');

    window.location.reload();
  };

  return {
    browserLanguage,
    isAutoDetected,
    setLanguageManually,
    resetLanguageDetection,
    forceLanguageDetection,
  };
}